import React, { forwardRef } from "react";

import { cn } from "../../../lib/utils";

import { ButtonContent, ButtonStandardProps, buttonStandardVariants } from "../ButtonStandard";
import { Link, LinkProps } from "../Link";

/**
 * Props for the ButtonLink component
 * Combines Link props with the button styling props from ButtonStandard
 */
export type ButtonLinkProps = Omit<LinkProps, "underline" | "className"> &
	Pick<ButtonStandardProps, "variant" | "color" | "size" | "iconLeft" | "iconRight" | "disabled" | "children">;

/**
 * ButtonLink Component
 *
 * A navigation link that looks like a button. It composes the Link component
 * for navigation behavior (client-side routing, external links, hard reloads)
 * and imports its styling from ButtonStandard, so it stays visually identical
 * to the other buttons while rendering an anchor.
 *
 * Use this instead of a ButtonStandard with an onClick that navigates, or an
 * anchor tag with hand-rolled button styling.
 */
const ButtonLink = forwardRef<HTMLAnchorElement, ButtonLinkProps>(
	(
		{
			variant = "solid",
			color = "primary",
			size = "medium",
			dangerouslySetClassName,
			iconLeft,
			iconRight,
			children,
			disabled = false,
			tabIndex,
			onClick,
			...linkProps
		},
		ref
	) => {
		// Anchors have no native disabled state, so block activation manually
		const handleClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
			if (disabled) {
				event.preventDefault();
				return;
			}
			onClick?.(event);
		};

		return (
			<Link
				{...linkProps}
				ref={ref}
				underline="never"
				dangerouslySetClassName={cn(
					buttonStandardVariants({ variant, color, size, disabled, className: dangerouslySetClassName })
				)}
				aria-disabled={disabled || undefined}
				tabIndex={disabled ? -1 : tabIndex}
				onClick={handleClick}
			>
				<ButtonContent variant={variant} color={color} size={size} iconLeft={iconLeft} iconRight={iconRight}>
					{children}
				</ButtonContent>
			</Link>
		);
	}
);

ButtonLink.displayName = "ButtonLink";

export { ButtonLink };
